import React, { useEffect, useState } from 'react';

import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Tooltip from '@material-ui/core/Tooltip';

import * as Api from '../../../api/api';
import { UiButton } from '../../../ui/ui-button';
import { UiDialog } from '../../../ui/ui-dialog';

import { employeeNameFromSimpleContact } from '../../../employees/utils/employee-name';
import { ICON_NAMES, MaterialDesignIcon } from '../../../util/material-design-icons';
import Message from '../../../widgets/legacy-message';
import { ReactComponent as LoadingLogo } from '../../../widgets/loading-logo.svg';
import { TableHeaderCell } from '../../../widgets/table-components/table-header-cell';
import { ContactType } from '../util/contact-type';
import { getDiscrepancyKeys, hasDataDiscrepancy } from '../util/has-data-discrepancy';
import { SimpleContactComparisonDialog } from '../util/simple-contact-comparison-dialog';

type InputProps = EmployeeDirectoryWizardStepProps;

const MatchingEmployeeRowWithDiscrepancies = ({
  discrepancyRow,
  resolutions,
  handleResolveDiscrepancy,
  clearResolution,
}: {
  discrepancyRow: ExternalIdMatchingEmployeeDirectoryImportRow;
  resolutions: PartiallyMatchingContactResolutions;
  handleResolveDiscrepancy: (
    importId: number,
    importedData: EmployeeDirectoryImportSimpleContact,
    existingData: PartiallyMatchingSimpleContact,
  ) => () => void;
  clearResolution: (importId: number) => () => void;
}) => {
  const { importId, importedData, existingData } = discrepancyRow;
  const discrepancyKeys = getDiscrepancyKeys(existingData, importedData);
  return (
    <TableRow key={`importId-${importId}`}>
      <TableCell>{employeeNameFromSimpleContact(importedData)}</TableCell>
      <TableCell>
        <ContactType context={existingData} />
      </TableCell>
      <TableCell>
        {discrepancyKeys.map((key) => (
          <div key={`import-${importId}-selected-${key}`} className="u-flex-center">
            {resolutions[importId]?.conflictResolution?.[key] || importedData[key]}
          </div>
        ))}
      </TableCell>
      <TableCell>
        {discrepancyKeys.map((key) => (
          <div key={`import-${importId}-overridden-${key}`} className="u-flex-center">
            {existingData[key]}
          </div>
        ))}
      </TableCell>
      <TableCell>
        {resolutions[importId] ? (
          <div className="u-flex-center">
            <MaterialDesignIcon iconName={ICON_NAMES.CHECK_CIRCLE} />
            <UiButton size="xxs" variant="tertiary" onClick={clearResolution(importId)}>
              Undo
            </UiButton>
          </div>
        ) : (
          <UiButton
            size="xs"
            onClick={handleResolveDiscrepancy(
              importId,
              importedData,
              existingData as PartiallyMatchingSimpleContact,
            )}
          >
            <MaterialDesignIcon iconName={ICON_NAMES.CLOSE} size="sm" />
            <span className="u-padding-left-sm">Edit</span>
          </UiButton>
        )}
      </TableCell>
    </TableRow>
  );
};

const MatchingCorrectEmployeeRow = ({
  discrepancyRow,
}: {
  discrepancyRow: ExternalIdMatchingEmployeeDirectoryImportRow;
}) => {
  const { importId, importedData, existingData } = discrepancyRow;
  return (
    <TableRow key={`importId-${importId}`}>
      <TableCell>{employeeNameFromSimpleContact(importedData)}</TableCell>
      <TableCell>
        <ContactType context={existingData} />
      </TableCell>
      <TableCell>
        <MaterialDesignIcon iconName={ICON_NAMES.CHECK_CIRCLE} />
      </TableCell>
      <TableCell>
        <MaterialDesignIcon iconName={ICON_NAMES.CHECK_CIRCLE} />
      </TableCell>
      <TableCell />
    </TableRow>
  );
};

export const EmployeeDirectoryImportWizardExternalIdMatchingEmployees = (props: InputProps) => {
  const { employeeDirectoryImportId, previousStepData, onNext, onCancel, PageIndicator } = props;

  const [matchingEmployeeData, setMatchingEmployeeData] = useState<{
    [importId: string]: ExternalIdMatchingEmployeeDirectoryImportRow;
  } | null>(null);
  const [rowToResolve, setRowToResolve] = useState<{
    importId: number;
    existingData: PartiallyMatchingSimpleContact;
    importedData: EmployeeDirectoryImportSimpleContact;
    context: any;
  } | null>(null);
  const [resolutions, setResolutions] = useState<PartiallyMatchingContactResolutions>({});
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [hasInitialized, setHasInitialized] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [limitRows, setLimitRows] = useState<boolean>(true);
  const [shouldForceRehire, setShouldForceRehire] = useState(false);

  useEffect(() => {
    if (
      previousStepData?.columnMappings &&
      !Object.values(previousStepData.columnMappings).includes('externalId')
    ) {
      onNext();
    }

    if (!hasInitialized) {
      Api.get(
        `/employee-directory-import/${employeeDirectoryImportId}/external-id-matching-employees`,
      )
        .then((resp) => {
          resp
            .json()
            .then((data: { [importId: string]: ExternalIdMatchingEmployeeDirectoryImportRow }) => {
              if (data.errorMessage || typeof data !== 'object') {
                setErrorMessage('Failed to load data');
              } else if (Object.keys(data).length === 0) {
                onNext();
              } else {
                setMatchingEmployeeData(data);
              }
            });
        })
        .catch(() => {
          setErrorMessage('Failed to load data');
        });
      setHasInitialized(true);
    }
  }, [hasInitialized, employeeDirectoryImportId, onNext, previousStepData?.columnMappings]);

  const handleOnNext = async () => {
    let response = null;
    setIsSaving(true);
    try {
      response = await Api.post(
        `/employee-directory-import/${employeeDirectoryImportId}/external-id-matching-employees`,
        {
          body: JSON.stringify({ resolutions, shouldForceRehire }),
        },
      );
    } catch (e) {
      setErrorMessage('There was an error saving data');
    }
    const result = await response?.json();
    setIsSaving(false);
    if (!result || result.errorMessage) {
      setErrorMessage('There was an error saving data');
    } else {
      onNext();
    }
  };

  const closeResolveDiscrepancyDialog = (
    conflictResolution?: EmployeeDirectoryImportSimpleContact,
  ) => {
    if (conflictResolution && rowToResolve) {
      setResolutions({
        ...resolutions,
        [rowToResolve.importId]: {
          ...resolutions[rowToResolve.importId],
          conflictResolution,
        },
      });
    }
    setRowToResolve(null);
  };

  const handleResolveDiscrepancy =
    (
      importId: number,
      importedData: EmployeeDirectoryImportSimpleContact,
      existingData: PartiallyMatchingSimpleContact,
    ) =>
    () => {
      setRowToResolve({
        importId,
        importedData,
        existingData,
        context: {
          employeeId: existingData.employeeId,
          jobApplicantId: existingData.jobApplicantId,
          referralId: existingData.referralId,
        },
      });
    };

  const clearResolution = (importId: number) => () => {
    const newResolutions = {
      ...resolutions,
    };
    delete newResolutions[importId];
    setResolutions(newResolutions);
  };

  const handleClickViewMoreRows = () => setLimitRows(false);

  const handleForceRehireCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShouldForceRehire(event.target.checked);
  };

  const isLoading = !matchingEmployeeData && !errorMessage;
  const matchingEmployees = matchingEmployeeData ? Object.values(matchingEmployeeData) : [];

  const { matchingDataDiscrepancyRows, matchingCorrectRows } = matchingEmployees.reduce(
    (acc, matchingRow) => {
      if (hasDataDiscrepancy(matchingRow.existingData, matchingRow.importedData)) {
        return {
          ...acc,
          matchingDataDiscrepancyRows: acc.matchingDataDiscrepancyRows.concat([matchingRow]),
        };
      }
      return {
        ...acc,
        matchingCorrectRows: acc.matchingCorrectRows.concat([matchingRow]),
      };
    },
    {
      matchingDataDiscrepancyRows: [] as Array<ExternalIdMatchingEmployeeDirectoryImportRow>,
      matchingCorrectRows: [] as Array<ExternalIdMatchingEmployeeDirectoryImportRow>,
    },
  );

  return (
    <>
      <UiDialog.Content>
        <PageIndicator />
        <span className="typo-h5">Employee Identifier Matching Employees</span>
        <p className="typo-subtitle">
          Every person on this page has matched an existing employee record on their Employee
          Identifier. You may want to edit any discrepancy in data between our record and the import
          data.
        </p>
        {errorMessage ? <Message variant="error">{errorMessage}</Message> : null}
        {isLoading ? (
          <div className="u-textAlign-center">
            <LoadingLogo />
          </div>
        ) : null}
        {matchingEmployees.length > 0 ? (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableHeaderCell>Name</TableHeaderCell>
                <TableHeaderCell>Contact Type</TableHeaderCell>
                <TableHeaderCell>New Values</TableHeaderCell>
                <TableHeaderCell>Old Values</TableHeaderCell>
                <TableHeaderCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {matchingDataDiscrepancyRows.map((discrepancyRow) => (
                <MatchingEmployeeRowWithDiscrepancies
                  discrepancyRow={discrepancyRow}
                  resolutions={resolutions}
                  handleResolveDiscrepancy={handleResolveDiscrepancy}
                  clearResolution={clearResolution}
                />
              ))}
              {matchingCorrectRows.length && !limitRows
                ? matchingCorrectRows.map((correctRow) => (
                    <MatchingCorrectEmployeeRow discrepancyRow={correctRow} />
                  ))
                : null}
              {matchingCorrectRows.length && limitRows ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    {limitRows ? (
                      <div
                        className="typo-link"
                        role="presentation"
                        onClick={handleClickViewMoreRows}
                      >
                        Matched {matchingCorrectRows.length} rows with no issues. View them now...
                      </div>
                    ) : null}
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        ) : null}
        {rowToResolve ? (
          <SimpleContactComparisonDialog
            row={rowToResolve}
            onClose={closeResolveDiscrepancyDialog}
          />
        ) : null}
      </UiDialog.Content>
      <UiDialog.Actions>
        <Tooltip title="Always rehire employees regardless of other conditions. Use this to make the directory look exactly like the uploaded sheet.">
          <FormControlLabel
            label="Force rehire employees?"
            control={
              <Checkbox checked={shouldForceRehire} onChange={handleForceRehireCheckboxChange} />
            }
          />
        </Tooltip>
        <div className="u-display-flex u-gap-10px">
          <UiButton variant="dismiss" onClick={onCancel}>
            Cancel
          </UiButton>
          <UiButton variant="primary" onClick={handleOnNext} loading={isSaving}>
            Save
          </UiButton>
        </div>
      </UiDialog.Actions>
    </>
  );
};
